import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { SellerBalanceById } from "@/components/SellerBalance";
import { PageHeader, DataTable, EmptyState } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Scale, Upload, Plus, Settings, ExternalLink, Check, X } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import { can } from "@/lib/permissions";

const PAGE_SIZE = 50;
const inr = (n) => `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const kg = (n) => (n == null ? "—" : `${Number(n)} kg`);
const dims = (d) => (d && d.length_cm ? `${d.length_cm}×${d.breadth_cm}×${d.height_cm}` : "—");
const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const TABS = [
  { id: "action_required", label: "Action Required" },
  { id: "disputed", label: "Disputed" },
  { id: "accepted", label: "Accepted" },
  { id: "resolved", label: "Resolved" },
  { id: "all", label: "All" },
];
const WD_STATUS = {
  ACTION_REQUIRED: { label: "Action required", cls: "bg-amber-100 text-amber-800" },
  DISPUTED: { label: "Disputed", cls: "bg-blue-100 text-blue-800" },
  ACCEPTED: { label: "Accepted", cls: "bg-slate-100 text-slate-700" },
  AUTO_ACCEPTED: { label: "Auto-accepted", cls: "bg-slate-100 text-slate-700" },
  RESOLVED_UPHELD: { label: "Upheld — charged", cls: "bg-red-100 text-red-700" },
  RESOLVED_REVERSED: { label: "Reversed", cls: "bg-green-100 text-green-700" },
};
const WdStatus = ({ s }) => {
  const c = WD_STATUS[s] || { label: s, cls: "bg-slate-100 text-slate-700" };
  return <Badge className={`${c.cls} border-0`}>{c.label}</Badge>;
};

const B2CWeightDiscrepancy = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const canManage = can(user, "b2c.weight_discrepancy:manage");

  const [activeTab, setActiveTab] = useLocalStorageState("admin-wd:tab", "disputed");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  // Modals
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ awb: "", applied_weight_kg: "", reason: "", upstream_extra_charge: "", charge_amount: "" });
  const [creating, setCreating] = useState(false);

  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveRemarks, setResolveRemarks] = useState("");
  const [resolving, setResolving] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [windowDays, setWindowDays] = useState(5);
  const [savingSettings, setSavingSettings] = useState(false);

  const tabGroup = (TABS.find((t) => t.id === activeTab) || TABS[0]).id;

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, status_group: tabGroup };
      const { data: res } = await axios.get(`${API}/b2c/admin/weight-discrepancies`, { params });
      setData({ items: res.items || [], total: res.total || 0 });
      if (res.counts) setCounts(res.counts);
    } catch (e) {
      toast.error(errDetail(e, "Failed to load weight discrepancies"));
    } finally {
      setLoading(false);
    }
  }, [page, tabGroup]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [activeTab]);

  // Load the current dispute window once so the button shows it without opening the modal.
  useEffect(() => {
    axios.get(`${API}/b2c/admin/weight-discrepancies/settings`)
      .then((r) => setWindowDays(r.data?.dispute_window_days || 5))
      .catch(() => {});
  }, []);

  const openSettings = async () => {
    try {
      const { data: s } = await axios.get(`${API}/b2c/admin/weight-discrepancies/settings`);
      setWindowDays(s.dispute_window_days || 5);
    } catch { /* default */ }
    setSettingsOpen(true);
  };
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/b2c/admin/weight-discrepancies/settings`, { dispute_window_days: Number(windowDays) });
      toast.success("Dispute window updated");
      setSettingsOpen(false);
    } catch (e) {
      toast.error(errDetail(e, "Could not update settings"));
    } finally {
      setSavingSettings(false);
    }
  };

  const doImport = async () => {
    if (!importFile) { toast.error("Choose a CSV/XLSX file"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data: res } = await axios.post(`${API}/b2c/admin/weight-discrepancies/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res);
      toast.success(`Imported: ${res.created} created, ${res.skipped} skipped, ${res.failed} failed`);
      fetchList();
    } catch (e) {
      toast.error(errDetail(e, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  const doCreate = async () => {
    if (!createForm.awb.trim() || !createForm.applied_weight_kg) {
      toast.error("AWB and applied weight are required");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        awb: createForm.awb.trim(),
        applied_weight_kg: Number(createForm.applied_weight_kg),
      };
      if (createForm.reason.trim()) payload.reason = createForm.reason.trim();
      if (createForm.upstream_extra_charge) payload.upstream_extra_charge = Number(createForm.upstream_extra_charge);
      if (createForm.charge_amount) payload.charge_amount = Number(createForm.charge_amount);
      await axios.post(`${API}/b2c/admin/weight-discrepancies`, payload);
      toast.success("Discrepancy raised");
      setCreateOpen(false);
      setCreateForm({ awb: "", applied_weight_kg: "", reason: "", upstream_extra_charge: "", charge_amount: "" });
      fetchList();
    } catch (e) {
      toast.error(errDetail(e, "Could not create the discrepancy"));
    } finally {
      setCreating(false);
    }
  };

  const doResolve = async (outcome) => {
    if (!resolveTarget) return;
    setResolving(true);
    try {
      await axios.post(`${API}/b2c/admin/weight-discrepancies/${resolveTarget.id}/resolve`, {
        outcome, remarks: resolveRemarks.trim() || undefined,
      });
      toast.success(outcome === "uphold" ? "Upheld — seller charged" : "Reversed — no charge");
      setResolveTarget(null);
      setResolveRemarks("");
      fetchList();
    } catch (e) {
      toast.error(errDetail(e, "Could not resolve the dispute"));
    } finally {
      setResolving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const tabCount = (id) => (id === "all" ? counts.all ?? data.total : counts[id]);

  const columns = [
    {
      key: "wd", header: "Discrepancy", primary: true,
      cell: (r) => (
        <div className="text-sm">
          <div className="font-mono text-[13px] text-slate-700">{r.wd_number}</div>
          <button onClick={() => navigate(`/admin/b2c/orders/${r.order_id}`)} className="text-xs text-brand hover:underline">{r.order_number}</button>
        </div>
      ),
    },
    { key: "seller", header: "Seller", cell: (r) => (
      <div>
        <span className="text-sm font-medium text-slate-800">{r.seller_brand_name || r.seller_id}</span>
        <SellerBalanceById sellerId={r.seller_id} />
      </div>
    ) },
    { key: "awb", header: "AWB", hideOnMobile: true, cell: (r) => <span className="text-sm font-mono text-slate-600">{r.awb || "—"}</span> },
    {
      key: "declared", header: "Declared", align: "right",
      cell: (r) => <div className="text-sm text-right"><div className="tabular-nums text-slate-700">{kg(r.declared_weight_kg)}</div><div className="text-xs text-slate-400">{dims(r.declared_dims)}</div></div>,
    },
    {
      key: "applied", header: "Applied", align: "right",
      cell: (r) => <div className="text-sm text-right"><div className="tabular-nums font-semibold text-slate-900">{kg(r.applied_weight_kg)}</div><div className="text-xs text-amber-600">+{Number(r.extra_weight_kg)} kg</div></div>,
    },
    { key: "charge", header: "Charge", align: "right", cell: (r) => <span className="text-sm font-semibold tabular-nums text-slate-800">{inr(r.charge_amount)}</span> },
    {
      key: "margin", header: "Margin", align: "right", hideOnMobile: true,
      cell: (r) => r.margin != null ? <span className={`text-sm tabular-nums ${r.margin >= 0 ? "text-green-700" : "text-red-600"}`}>{inr(r.margin)}</span> : <span className="text-slate-400 text-sm">—</span>,
    },
    { key: "status", header: "Status", cell: (r) => <WdStatus s={r.status} /> },
    {
      key: "actions", header: "Actions", isActions: true,
      cell: (r) => r.status === "DISPUTED" && canManage
        ? <Button size="sm" onClick={() => { setResolveTarget(r); setResolveRemarks(""); }} data-testid={`wd-review-${r.id}`}>Review</Button>
        : null,
    },
  ];

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Weight Discrepancies"
          subtitle="Cross-seller courier re-weigh charges — import re-weighs, review disputes, resolve"
          actions={canManage ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={openSettings}><Settings className="w-4 h-4 mr-1.5" /> Window · {windowDays}d</Button>
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}><Plus className="w-4 h-4 mr-1.5" /> Add</Button>
              <Button size="sm" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }}><Upload className="w-4 h-4 mr-1.5" /> Import</Button>
            </div>
          ) : undefined}
        />

        <div className="flex gap-2 border-b border-slate-200 bg-white px-2 sm:px-6 rounded-t-lg shadow-sm overflow-x-auto">
          {TABS.map((tab) => {
            const c = tabCount(tab.id);
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`admin-wd-tab-${tab.id}`}
                className={`px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id ? "border-brand text-brand" : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                }`}>
                {tab.label}{c != null && <span className="font-bold text-brand"> ({c})</span>}
              </button>
            );
          })}
        </div>

        <DataTable
          columns={columns}
          data={data.items}
          rowKey={(r) => r.id}
          loading={loading}
          empty={<EmptyState icon={Scale} title="No weight discrepancies" description="Import an upstream reconciliation file, or add one manually, to start." />}
          pagination={totalPages > 1 ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage } : undefined}
        />
      </div>

      {/* Import modal */}
      <Dialog open={importOpen} onOpenChange={(v) => !v && setImportOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Import re-weighs (CSV / XLSX)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Columns: <span className="font-mono text-xs">awb, applied_weight_kg</span> (required) · <span className="font-mono text-xs">length_cm, breadth_cm, height_cm, reason, upstream_extra_charge</span> (optional). One discrepancy per order is created; repeats are skipped.</p>
            <input type="file" accept=".csv,.xlsx" onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm" data-testid="wd-import-file" />
            {importResult && (
              <div className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex gap-4">
                  <span className="text-green-700 font-medium">{importResult.created} created</span>
                  <span className="text-amber-700">{importResult.skipped} skipped</span>
                  <span className="text-red-600">{importResult.failed} failed</span>
                </div>
                {(importResult.results || []).filter((r) => r.status !== "created").slice(0, 8).map((r, i) => (
                  <div key={i} className="mt-1 text-xs text-slate-500">Row {r.row} ({r.awb}): {r.error}</div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>Close</Button>
            <Button onClick={doImport} disabled={!importFile || importing}>{importing ? "Importing…" : "Import"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual create modal */}
      <Dialog open={createOpen} onOpenChange={(v) => !v && setCreateOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add a weight discrepancy</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">AWB <span className="text-red-500">*</span></Label>
              <Input value={createForm.awb} onChange={(e) => setCreateForm({ ...createForm, awb: e.target.value })} placeholder="Shipment AWB" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Applied weight (kg) <span className="text-red-500">*</span></Label>
                <Input type="number" step="0.01" min="0" value={createForm.applied_weight_kg} onChange={(e) => setCreateForm({ ...createForm, applied_weight_kg: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Upstream charge (₹)</Label>
                <Input type="number" step="0.01" min="0" value={createForm.upstream_extra_charge} onChange={(e) => setCreateForm({ ...createForm, upstream_extra_charge: e.target.value })} placeholder="cost to us" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Charge override (₹)</Label>
              <Input type="number" step="0.01" min="0" value={createForm.charge_amount} onChange={(e) => setCreateForm({ ...createForm, charge_amount: e.target.value })} placeholder="blank = auto from rate card" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Reason</Label>
              <Input value={createForm.reason} onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })} placeholder="courier remark" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button onClick={doCreate} disabled={creating}>{creating ? "Saving…" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve dispute modal */}
      <Dialog open={!!resolveTarget} onOpenChange={(v) => !v && setResolveTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Review dispute — {resolveTarget?.wd_number}</DialogTitle></DialogHeader>
          {resolveTarget && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-slate-500">Declared</p><p className="font-medium">{kg(resolveTarget.declared_weight_kg)} ({dims(resolveTarget.declared_dims)})</p></div>
                <div><p className="text-xs text-slate-500">Courier charged</p><p className="font-medium">{kg(resolveTarget.applied_weight_kg)}</p></div>
                <div><p className="text-xs text-slate-500">Seller charge</p><p className="font-medium">{inr(resolveTarget.charge_amount)}</p></div>
                <div><p className="text-xs text-slate-500">Upstream cost</p><p className="font-medium">{resolveTarget.upstream_extra_charge != null ? inr(resolveTarget.upstream_extra_charge) : "—"}</p></div>
              </div>
              {resolveTarget.dispute && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <p><span className="text-slate-500">Category:</span> {resolveTarget.dispute.category}</p>
                  <p><span className="text-slate-500">Remarks:</span> {resolveTarget.dispute.remarks}</p>
                  {resolveTarget.dispute.product_url && (
                    <a href={resolveTarget.dispute.product_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline">
                      Product <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {(resolveTarget.dispute.evidence_urls || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {resolveTarget.dispute.evidence_urls.map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand hover:underline border border-slate-200 rounded px-2 py-1">
                          Evidence {i + 1} <ExternalLink className="w-3 h-3" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Resolution remarks</Label>
                <Textarea rows={2} value={resolveRemarks} onChange={(e) => setResolveRemarks(e.target.value)} placeholder="Courier decision / reason (shown to the seller)" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="text-green-700 hover:bg-green-50" onClick={() => doResolve("reverse")} disabled={resolving}>
              <Check className="w-4 h-4 mr-1.5" /> Reverse (no charge)
            </Button>
            <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => doResolve("uphold")} disabled={resolving}>
              <X className="w-4 h-4 mr-1.5" /> Uphold (charge seller)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings modal */}
      <Dialog open={settingsOpen} onOpenChange={(v) => !v && setSettingsOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Dispute window</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Days a seller has to accept or dispute before a discrepancy is auto-accepted and charged. Keep this shorter than the upstream window (Shiprocket = 7 days) to leave buffer to relay disputes.</p>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Window (days)</Label>
              <Input type="number" min="1" max="30" value={windowDays} onChange={(e) => setWindowDays(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)} disabled={savingSettings}>Cancel</Button>
            <Button onClick={saveSettings} disabled={savingSettings}>{savingSettings ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default B2CWeightDiscrepancy;
